import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import { endpoints } from '@/api/endpoints';
import { ErrorState, LoadingState } from '@/components/states';
import { FloorPlanEditor, type PlacedItem } from '@/components/floor-plan';
import { computeBackgroundHash } from '@/components/floor-plan/metrics';
import { readAutosavedProject, removeAutosavedProject, writeAutosavedProject } from '@/components/floor-plan/projectAutosave';
import type { FloorPlanEditorHandle } from '@/components/floor-plan/FloorPlanEditor';
import { parseProjectFile, serializeProject, validateBackgroundDataUrl } from '@/components/floor-plan/projectFile';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { formatCircuitLabel, formatLoadCategory, formatRcdGroupLabel } from '@/lib/electricalLabels';
import type { CalcGenerateRequest, CalcGenerateResponse, Circuit, ComplianceIssue, PreviewPayload, ProjectType, RCDGroup, SupplyType } from '@/types/api';

const MANUAL_APPLIANCES_MAX_LENGTH = 2000;
const MANUAL_APPLIANCES_MAX_COUNT = 70;
const PROJECT_FILE_MAX_SIZE = 30 * 1024 * 1024;
const AUTOSAVE_DEBOUNCE_MS = 700;

const RCD_STRATEGIES = [
  { value: 'recommended_rebt', labelKey: 'manual.rcd_recommended' },
  { value: 'minimal_rebt', labelKey: 'manual.rcd_minimal' },
  { value: 'comfort_rebt', labelKey: 'manual.rcd_comfort' },
];

function supportsSplitC4(strategy: string) {
  return strategy === 'recommended_rebt' || strategy === 'comfort_rebt';
}

interface ManualPageState {
  calcResult?: CalcGenerateResponse;
  generationMode?: string;
}

function isCalcGenerateResponse(value: unknown): value is CalcGenerateResponse {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<CalcGenerateResponse>;
  return (
    Boolean(result.preview) &&
    Boolean(result.layout) &&
    Array.isArray(result.circuits) &&
    Array.isArray(result.groups) &&
    Array.isArray(result.issues) &&
    Array.isArray(result.warnings)
  );
}

const FLOOR_PLAN_APPLIANCE_MAP: Record<string, string | null> = {
  lighting_single: 'lighting',
  exhaust_fan: 'lighting',
  lighting_spot: 'lighting',
  socket_single: 'socket',
  socket_double: 'socket',
  socket_triple: 'socket',
  socket_quadruple: 'socket',
  socket_3ph: 'socket',
  // TODO(B10): confirm whether room socket symbols should map to dedicated appliance tokens or socket metadata.
  bathroom_socket: 'bathroom_socket',
  kitchen_socket: 'kitchen_socket',
  // Weak-current outlets: planning markers only, not part of the power calculation.
  tv_socket: null,
  rj45_socket: null,
  washing_machine: 'washing_machine',
  oven: 'oven',
  hob: 'induction_hob',
  microwave: 'microwave',
  extractor_hood: 'extractor_hood',
  refrigerator: 'refrigerator',
  freezer: 'freezer',
  air_conditioner: 'air_conditioner',
  boiler: 'boiler',
  dryer: 'dryer',
  electric_heater: 'electric_heater',
  ev_charger: 'ev_charger',
  hydromassage_bath: 'hydromassage_bath',
  dishwasher: 'dishwasher',
  // Backend token -> REBT C11 (automation / energy / security).
  automation: 'automation',
  switch: null,
  switch_double: null,
};

function getManualApplianceMapping(items: PlacedItem[]) {
  const appliances: string[] = [];
  const unmappedTypes = new Set<string>();
  let hasAirConditioner = false;

  items.forEach(item => {
    if (!(item.type in FLOOR_PLAN_APPLIANCE_MAP)) {
      unmappedTypes.add(item.type);
      return;
    }

    // Multiple air_conditioner icons on the floor plan represent indoor unit locations/zones.
    // Count them as one electrical AC system by default; the electrician can adjust the final scheme later.
    if (item.type === 'air_conditioner') {
      hasAirConditioner = true;
      return;
    }

    const appliance = FLOOR_PLAN_APPLIANCE_MAP[item.type];
    if (appliance) appliances.push(appliance);
  });

  if (hasAirConditioner) appliances.push('air_conditioner');

  return {
    appliances,
    unmappedTypes: Array.from(unmappedTypes),
  };
}

function isC3Circuit(circuit: Circuit): boolean {
  return [circuit.rebt_code, circuit.circuit_id, circuit.label]
    .some(value => /\bC3\b/i.test(value ?? ''));
}

function normalizeLoadToken(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

function circuitHasLoadToken(circuit: Circuit, tokens: readonly string[]): boolean {
  if (circuit.load_ids.length === 0) return false;
  return circuit.load_ids.some(loadId => {
    const normalized = normalizeLoadToken(loadId);
    return tokens.some(token => normalized.includes(token));
  });
}

function isComfortGenerationMode(generationMode: string | null | undefined): boolean {
  return generationMode?.toLowerCase().includes('comfort') ?? false;
}

function isIntentionalC3SplitCircuit(circuit: Circuit): boolean {
  const candidate = normalizeLoadToken(circuit.circuit_id.replace(/^circuit_/, ''));
  return candidate === 'rebt_c3_oven';
}

function shouldShowC3OvenOnlyAdvisory(circuit: Circuit, generationMode?: string): boolean {
  if (isComfortGenerationMode(generationMode)) return false;
  if (isIntentionalC3SplitCircuit(circuit)) return false;
  if (!isC3Circuit(circuit)) return false;
  const hasOven = circuitHasLoadToken(circuit, ['oven', 'horno']);
  if (!hasOven) return false;
  return !circuitHasLoadToken(circuit, [
    'hob',
    'cooktop',
    'placa',
    'vitro',
    'induction_hob',
    'electric_hob',
    'cocina',
    'cooker',
  ]);
}

function isUsablePreview(preview: PreviewPayload) {
  const data = preview.data.trim();
  if (!data) return false;
  const marker = data.toLowerCase();
  return marker !== 'placeholder' && marker !== 'empty' && marker !== 'null';
}

function InlinePreviewViewer({ preview }: { preview: PreviewPayload }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const contentType = preview.content_type.trim() || 'image/svg+xml';
  const canRender = contentType.startsWith('image/');

  useEffect(() => {
    if (!canRender || !isUsablePreview(preview)) {
      setObjectUrl(null);
      return;
    }

    const blob = new Blob([preview.data], { type: contentType });
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [canRender, contentType, preview]);

  if (!canRender || !isUsablePreview(preview)) {
    return (
      <div className="bg-surface border border-border rounded p-4 text-sm text-ink-4">
        Preview is not available for this calculation.
      </div>
    );
  }

  if (!objectUrl) {
    return <LoadingState label="Loading preview..." />;
  }

  return (
    <div className="bg-white rounded p-4 overflow-auto">
      <img
        src={objectUrl}
        alt="Manual calculator preview"
        className="w-full h-auto max-h-[620px] object-contain"
      />
    </div>
  );
}

function CircuitList({ circuits, generationMode }: { circuits: Circuit[]; generationMode?: string }) {
  const { t } = useTranslation();
  if (!circuits.length) {
    return <div className="text-xs text-ink-4">{t('manual.no_circuits')}</div>;
  }
  return (
    <div className="space-y-2">
      {circuits.map(circuit => (
        <div key={circuit.circuit_id} className="border border-border rounded p-3 bg-surface text-xs">
          <div className="font-sans text-xs font-medium text-ink">{formatCircuitLabel(t, circuit)}</div>
          <div className="mt-1 text-ink-3">
            {formatLoadCategory(t, circuit.category)} · {circuit.protection_device_id}
          </div>
          {shouldShowC3OvenOnlyAdvisory(circuit, generationMode) && (
            <div className="no-print print:hidden mt-3 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-ink-3">
              <div className="font-sans text-xs font-semibold text-warning">
                {t('manual.c3_oven_only_advisory_title')}
              </div>
              <div className="mt-1 leading-relaxed">
                {t('manual.c3_oven_only_advisory_body')}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function GroupList({ groups }: { groups: RCDGroup[] }) {
  const { t } = useTranslation();
  if (!groups.length) {
    return <div className="text-xs text-ink-4">{t('manual.no_groups')}</div>;
  }
  return (
    <div className="space-y-2">
      {groups.map((group, groupIndex) => (
        <div key={group.group_id} className="border border-border rounded p-3 bg-surface text-xs">
          <div className="font-sans text-xs font-medium text-ink">{formatRcdGroupLabel(t, groupIndex)}</div>
          <div className="mt-1 text-ink-3">
            {group.protection_device_id} · {t('electrical.circuit_count', { count: group.circuit_ids.length })}
          </div>
        </div>
      ))}
    </div>
  );
}

function IssueList({ issues }: { issues: ComplianceIssue[] }) {
  const { t } = useTranslation();
  if (!issues.length) {
    return <div className="text-xs text-ink-4">{t('manual.no_issues')}</div>;
  }
  return (
    <div className="space-y-2">
      {issues.map(issue => (
        <div key={issue.issue_id} className="border-l-2 border-warning/60 bg-surface p-3 text-xs">
          <div className="font-mono text-[11px] text-ink-3">
            {issue.severity} · {issue.code}
          </div>
          <div className="mt-1 text-ink">{issue.message}</div>
        </div>
      ))}
    </div>
  );
}

function PowerSummary({ result }: { result: CalcGenerateResponse }) {
  const metrics = [
    { label: 'IGA', value: `${result.iga_rating_a} A` },
    { label: 'Power', value: `${result.planned_power_w} W` },
    { label: 'Grade', value: result.electrification_grade },
    { label: 'Voltage', value: `${result.voltage_v} V` },
  ];

  return (
    <div className="border border-border rounded p-4 bg-surface">
      <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
        Power forecast
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map(metric => (
          <div key={metric.label} className="rounded border border-border bg-page/40 p-3">
            <div className="text-[11px] font-medium text-ink-4">{metric.label}</div>
            <div className="mt-1 font-mono text-sm text-ink">{metric.value}</div>
          </div>
        ))}
      </div>

      {result.power_assumptions.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-medium text-ink-4 mb-2">
            Assumptions
          </div>
          <div className="space-y-1">
            {result.power_assumptions.map((assumption, i) => (
              <div key={`a-${i}`} className="text-xs text-ink-3">{assumption}</div>
            ))}
          </div>
        </div>
      )}

      {result.power_warnings.length > 0 && (
        <div className="mt-4 border border-warning/40 bg-warning/10 rounded p-3">
          <div className="text-[11px] font-medium text-warning mb-2">
            Power warnings
          </div>
          <div className="space-y-1">
            {result.power_warnings.map((warning, i) => (
              <div key={`w-${i}`} className="text-xs text-ink-3">{warning}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultPanel({ result, generationMode }: { result: CalcGenerateResponse; generationMode?: string }) {
  const { t } = useTranslation();
  const hasDxfDownload = result.dxf_download_url !== null;
  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-sans text-sm font-semibold text-ink">
          {t('manual.result')}
        </h2>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to="/manual/panel-board"
            state={{ layout: result.layout, calcResult: result, generationMode }}
            className="text-xs font-medium px-3 py-2 rounded
                       bg-surface text-ink border border-border hover:bg-surface-3 transition"
          >
            {t('panel_board.open_editor')}
          </Link>
          {hasDxfDownload && result.dxf_download_url && (
            <a
              href={result.dxf_download_url}
              className="text-xs font-medium px-3 py-2 rounded
                         bg-accent text-page hover:brightness-110 transition"
            >
              {t('manual.download_dxf')}
            </a>
          )}
        </div>
      </div>

      <InlinePreviewViewer preview={result.preview} />

      <PowerSummary result={result} />

      {result.warnings.length > 0 && (
        <div className="border border-warning/40 bg-warning/10 rounded p-4">
          <h3 className="font-sans text-sm font-semibold text-warning mb-2">
            {t('manual.warnings')}
          </h3>
          <div className="space-y-1">
            {result.warnings.map((warning, i) => (
              <div key={`w-${i}`} className="text-xs text-ink-3">{warning}</div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('manual.circuits')} ({result.circuits.length})
          </h3>
          <CircuitList circuits={result.circuits} generationMode={generationMode} />
        </div>
        <div>
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('manual.groups', 'Groups')} ({result.groups.length})
          </h3>
          <GroupList groups={result.groups} />
        </div>
        <div>
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('manual.issues')} ({result.issues.length})
          </h3>
          <IssueList issues={result.issues} />
        </div>
      </div>
    </div>
  );
}

export function ManualPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const routeState = location.state as ManualPageState | null;
  const restoredResult = isCalcGenerateResponse(routeState?.calcResult) ? routeState.calcResult : null;
  const restoredGenerationMode = typeof routeState?.generationMode === 'string' ? routeState.generationMode : null;
  const editorRef = useRef<FloorPlanEditorHandle>(null);
  const openProjectInputRef = useRef<HTMLInputElement>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const latestAutosaveJsonRef = useRef<string | null>(null);
  const latestBackgroundRef = useRef<string | null>(null);
  const placedItemsRef = useRef<PlacedItem[]>([]);
  const skipNextAutosaveRef = useRef(false);
  const autosaveClearedRef = useRef(false);
  const backgroundClearedRef = useRef(false);
  const autosaveSeqRef = useRef(0);
  const [placedItems, setPlacedItems] = useState<PlacedItem[]>([]);
  const [printDisclaimerOpen, setPrintDisclaimerOpen] = useState(false);
  const [restoreComplete, setRestoreComplete] = useState(false);
  const [editorRevision, setEditorRevision] = useState(0);
  const [limitNotice, setLimitNotice] = useState<string | null>(null);
  const [supplyType, setSupplyType] = useState<SupplyType>('1ph');
  const [preferredRcdStrategy, setPreferredRcdStrategy] = useState('recommended_rebt');
  const [splitC4, setSplitC4] = useState(true);
  const [areaM2, setAreaM2] = useState('');
  const projectType: ProjectType = 'apartment';
  const electrificationGrade = '';
  const plannedPowerW = '';
  const voltageV = '230';

  const mutation = useMutation({
    mutationFn: (body: CalcGenerateRequest) => endpoints.calcGenerate(body),
  });
  const visibleResult = mutation.isSuccess ? mutation.data : (!mutation.isPending && !mutation.isError ? restoredResult : null);
  const visibleGenerationMode = mutation.isSuccess
    ? mutation.variables?.preferred_rcd_strategy ?? preferredRcdStrategy
    : restoredGenerationMode ?? preferredRcdStrategy;

  useEffect(() => {
    placedItemsRef.current = placedItems;
  }, [placedItems]);

  const buildProjectJson = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return null;

    const editorState = editor.getState();
    const exportedBackground = await editor.exportBackgroundDataUrl();
    const background = exportedBackground ?? (
      backgroundClearedRef.current ? null : latestBackgroundRef.current
    );
    if (exportedBackground !== null) {
      latestBackgroundRef.current = exportedBackground;
      backgroundClearedRef.current = false;
    }
    return serializeProject({
      ...editorState,
      items: placedItemsRef.current,
      background,
      backgroundHash: background === null ? null : computeBackgroundHash(background),
    });
  }, []);

  const saveAutosaveNow = useCallback(async () => {
    const saveSeq = autosaveSeqRef.current + 1;
    autosaveSeqRef.current = saveSeq;
    const json = await buildProjectJson();
    if (!json) return;
    if (saveSeq !== autosaveSeqRef.current) return;

    latestAutosaveJsonRef.current = json;
    autosaveClearedRef.current = false;
    writeAutosavedProject(json);
  }, [buildProjectJson]);

  const handleEditorProjectStateChange = useCallback(() => {
    setEditorRevision(value => value + 1);
  }, []);

  const handleClearProject = useCallback(() => {
    skipNextAutosaveRef.current = true;
    autosaveClearedRef.current = true;
    backgroundClearedRef.current = true;
    autosaveSeqRef.current += 1;
    latestAutosaveJsonRef.current = null;
    latestBackgroundRef.current = null;
    removeAutosavedProject();
  }, []);

  const handleBackgroundCleared = useCallback(() => {
    backgroundClearedRef.current = true;
    autosaveSeqRef.current += 1;
    latestBackgroundRef.current = null;
    setEditorRevision(value => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restoreAutosave = async () => {
      const text = readAutosavedProject();
      if (!text) {
        skipNextAutosaveRef.current = true;
        if (!cancelled) setRestoreComplete(true);
        return;
      }

      const parsed = parseProjectFile(text);
      if (!parsed.ok) {
        removeAutosavedProject();
        skipNextAutosaveRef.current = true;
        if (!cancelled) setRestoreComplete(true);
        return;
      }

      if (parsed.project.background !== null) {
        const okBg = await validateBackgroundDataUrl(parsed.project.background);
        if (!okBg) parsed.project.background = null;
      }
      if (parsed.project.background === null) parsed.project.backgroundHash = null;

      if (cancelled) return;

      latestBackgroundRef.current = parsed.project.background;
      latestAutosaveJsonRef.current = serializeProject(parsed.project);
      autosaveClearedRef.current = false;
      backgroundClearedRef.current = parsed.project.background === null;
      editorRef.current?.loadState(parsed.project);
      setRestoreComplete(true);
    };

    void restoreAutosave();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!restoreComplete) return;

    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }

    autosaveClearedRef.current = false;

    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      void saveAutosaveNow();
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [editorRevision, placedItems, restoreComplete, saveAutosaveNow]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!restoreComplete) return;
      if (autosaveClearedRef.current) return;

      const editor = editorRef.current;
      if (!editor) return;

      const editorState = editor.getState();
      const background = backgroundClearedRef.current ? null : latestBackgroundRef.current;
      const json = serializeProject({
        ...editorState,
        items: placedItemsRef.current,
        background,
        backgroundHash: background === null ? null : computeBackgroundHash(background),
      });
      latestAutosaveJsonRef.current = json;
      writeAutosavedProject(json);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [restoreComplete]);

  const { appliances, unmappedTypes } = getManualApplianceMapping(placedItems);
  const serializedAppliances = appliances.join(',');
  const isInputTooLong = serializedAppliances.length > MANUAL_APPLIANCES_MAX_LENGTH;
  const hasTooManyAppliances = appliances.length > MANUAL_APPLIANCES_MAX_COUNT;
  const parseNullableNumber = (value: string, field: string) => {
    const trimmed = value.trim();
    if (!trimmed) return { value: null, error: null };
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return { value: null, error: `${field} must be a finite number.` };
    if (parsed < 0) return { value: null, error: `${field} must be zero or greater.` };
    return { value: parsed, error: null };
  };
  const plannedPower = parseNullableNumber(plannedPowerW, 'Planned power');
  const area = parseNullableNumber(areaM2, 'Area');
  const voltage = parseNullableNumber(voltageV, 'Voltage');
  const forecastValidationMessage = plannedPower.error ?? area.error ?? voltage.error;
  const validationMessage = isInputTooLong
    ? `Maximum ${MANUAL_APPLIANCES_MAX_LENGTH} characters.`
    : hasTooManyAppliances
      ? `Maximum ${MANUAL_APPLIANCES_MAX_COUNT} appliances.`
      : forecastValidationMessage
        ? forecastValidationMessage
      : null;

  const submit = () => {
    if (validationMessage || appliances.length === 0 || mutation.isPending) return;
    mutation.mutate({
      manual_appliances: appliances,
      project_type: projectType,
      supply_type: supplyType,
      preferred_rcd_strategy: preferredRcdStrategy,
      split_c4: supportsSplitC4(preferredRcdStrategy) ? splitC4 : null,
      electrification_grade: electrificationGrade ? electrificationGrade as CalcGenerateRequest['electrification_grade'] : null,
      planned_power_w: plannedPower.value,
      area_m2: area.value,
      voltage_v: voltage.value ?? 230,
    });
  };

  const handlePlacedItemsChange = (nextItems: PlacedItem[]) => {
    const currentCount = getManualApplianceMapping(placedItems).appliances.length;
    const nextCount = getManualApplianceMapping(nextItems).appliances.length;

    if (nextCount > MANUAL_APPLIANCES_MAX_COUNT && nextCount > currentCount) {
      setLimitNotice(`Maximum ${MANUAL_APPLIANCES_MAX_COUNT} calculator appliances. Switches are not counted.`);
      return;
    }

    setLimitNotice(null);
    setPlacedItems(nextItems);
  };

  const handleConfirmPrint = () => {
    setPrintDisclaimerOpen(false);
    // Let the dialog unmount before opening the browser print dialog.
    window.setTimeout(() => window.print(), 0);
  };

  const handleSaveProject = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    const editorState = editor.getState();
    const background = await editor.exportBackgroundDataUrl();
    const json = serializeProject({
      ...editorState,
      items: placedItems,
      background,
      backgroundHash: background === null ? null : computeBackgroundHash(background),
    });
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plan.pfplan.json';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleOpenProject = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    try {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > PROJECT_FILE_MAX_SIZE) {
        setLimitNotice('Project file is too large.');
        return;
      }

      const text = await file.text();
      const parsed = parseProjectFile(text);
      if (!parsed.ok) {
        setLimitNotice(parsed.error || 'Не удалось открыть файл проекта');
        return;
      }

      if (parsed.project.background !== null) {
        const okBg = await validateBackgroundDataUrl(parsed.project.background);
        if (!okBg) parsed.project.background = null;
      }
      if (parsed.project.background === null) parsed.project.backgroundHash = null;

      editorRef.current?.loadState(parsed.project);
      latestBackgroundRef.current = parsed.project.background;
      latestAutosaveJsonRef.current = serializeProject(parsed.project);
      autosaveClearedRef.current = false;
      backgroundClearedRef.current = parsed.project.background === null;
      autosaveSeqRef.current += 1;
      writeAutosavedProject(latestAutosaveJsonRef.current);
      setLimitNotice(null);
    } catch {
      setLimitNotice('Не удалось открыть файл проекта');
    } finally {
      input.value = '';
    }
  };

  return (
    <div className="animate-fade-up max-w-[1440px]">
      <div className="mb-6 max-w-5xl">
        <h1 className="font-sans text-xl font-semibold tracking-[-0.015em] mb-2">{t('manual.title')}</h1>
        <p className="text-sm text-ink-3">{t('manual.description')}</p>
      </div>

      <div className="border border-border rounded-lg p-5 bg-surface-2 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Field label={t('manual.area_m2')}>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={areaM2}
              onChange={e => setAreaM2(e.target.value)}
              placeholder="auto"
            />
          </Field>
          <Field label={t('manual.supply_type')}>
            <Select value={supplyType} onChange={e => setSupplyType(e.target.value as SupplyType)}>
              <option value="1ph">{t('manual.supply_1ph')}</option>
              <option value="2ph">{t('manual.supply_2ph')}</option>
            </Select>
          </Field>
          <Field label={t('manual.rcd_strategy')}>
            <Select
              value={preferredRcdStrategy}
              onChange={e => setPreferredRcdStrategy(e.target.value)}
            >
              {RCD_STRATEGIES.map(strategy => (
                <option key={strategy.value} value={strategy.value}>{t(strategy.labelKey)}</option>
              ))}
            </Select>
          </Field>
          {supportsSplitC4(preferredRcdStrategy) && (
            <label className="flex items-center gap-2 text-sm text-ink-3 md:col-span-3">
              <input
                type="checkbox"
                checked={splitC4}
                onChange={e => setSplitC4(e.target.checked)}
                className="h-4 w-4 rounded border-border-2 bg-surface"
                style={{ accentColor: 'rgb(var(--accent))' }}
              />
              {t('manual.split_appliances_by_lines')}
            </label>
          )}
        </div>
      </div>

      <div className="mb-3 flex justify-end gap-2">
        <input
          ref={openProjectInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={event => void handleOpenProject(event)}
        />
        <Button type="button" onClick={() => openProjectInputRef.current?.click()}>
          {t('manual.open_project')}
        </Button>
        <Button type="button" onClick={() => void handleSaveProject()}>
          {t('manual.save_project')}
        </Button>
        <Button type="button" onClick={() => setPrintDisclaimerOpen(true)}>
          {t('manual.download_print')}
        </Button>
      </div>

      <div data-print-area>
        <FloorPlanEditor
          ref={editorRef}
          items={placedItems}
          onItemsChange={handlePlacedItemsChange}
          onProjectStateChange={handleEditorProjectStateChange}
          onClearProject={handleClearProject}
          onBackgroundCleared={handleBackgroundCleared}
        />
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-ink-4">
        <span>{validationMessage ?? `${MANUAL_APPLIANCES_MAX_COUNT} appliances max`}</span>
        <span>{serializedAppliances.length}/{MANUAL_APPLIANCES_MAX_LENGTH}</span>
      </div>

      {limitNotice && (
        <div className="mt-2 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-ink-3">
          {limitNotice}
        </div>
      )}

      {unmappedTypes.length > 0 && (
        <div className="mt-2 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-ink-3">
          Some floor-plan elements are not supported by the calculator and will be ignored: {unmappedTypes.join(', ')}.
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="primary"
          type="button"
          onClick={() => editorRef.current?.openSpecification()}
        >
          {t('floor_plan.specification')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={submit}
          disabled={mutation.isPending || appliances.length === 0 || Boolean(validationMessage)}
        >
          {mutation.isPending ? t('manual.generating') : t('manual.generate')}
        </Button>
        <div className="text-[11px] text-ink-4">
          <span className="font-mono">{appliances.length}</span> {t('manual.appliance_count')}
        </div>
      </div>

      {mutation.isPending && <LoadingState label={t('manual.generating')} />}
      {mutation.isError && <div className="mt-6"><ErrorState error={mutation.error} onRetry={submit} /></div>}
      {visibleResult && <ResultPanel result={visibleResult} generationMode={visibleGenerationMode} />}

      {printDisclaimerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="print-disclaimer-title"
            className="w-full max-w-md rounded-lg border border-border bg-surface p-5 text-ink shadow-xl"
          >
            <h2 id="print-disclaimer-title" className="text-base font-semibold">
              {t('manual.disclaimer_title')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-ink-3">{t('manual.disclaimer_p1')}</p>
            <p className="mt-3 text-sm leading-relaxed text-ink-3">{t('manual.disclaimer_p2')}</p>
            <div className="mt-5 flex justify-end">
              <Button variant="primary" type="button" onClick={handleConfirmPrint}>
                {t('manual.disclaimer_confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
